import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import GradientText from "@/components/home/primitives/GradientText";
import SectionHeading from "@/components/home/primitives/SectionHeading";
import { SHIP_STEPS } from "@/data/marketing/homepage";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

export default function HomeUserManual() {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();

  return (
    <section className="w-full bg-white py-16 sm:py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="How it ships"
          title={
            <>
              Three steps. One hour. <GradientText>No developer.</GradientText>
            </>
          }
          subtitle="Setup is a chat conversation. That's the actual product. The genie that helps you build your business is the same one your customers will talk to."
        />

        <div
          ref={ref}
          className="mt-12 grid items-center gap-10 lg:grid-cols-2"
        >
          {/* Steps */}
          <div className="space-y-4 sm:space-y-5">
            {SHIP_STEPS.map((step, i) => (
              <div
                key={step.number}
                className={cn(
                  "flex gap-4 rounded-2xl border border-[#E5EAF0] bg-[#F9FAFC] p-5 transition-all duration-700 ease-out",
                  inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                )}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-white shadow-sm">
                  <GenieIcon
                    name={step.genieIcon}
                    size={28}
                    className="h-7 w-7"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold leading-none">
                      <GradientText>{step.number}</GradientText>
                    </span>
                    <h3 className="text-lg font-semibold text-[#222B45]">
                      {step.title}
                    </h3>
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Device mockup */}
          <div
            className={cn(
              "flex items-center justify-center transition-all duration-700 ease-out",
              inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            )}
            style={{ transitionDelay: "200ms" }}
          >
            <img
              src="/home-page-imagery/branded-drivefree-nobg.webp"
              alt="A branded Help Genie voice agent live on a mobile site"
              loading="lazy"
              className="w-full max-w-md object-contain drop-shadow-2xl"
            />
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
          <Link
            to="/signup"
            className="inline-flex items-center justify-center rounded-xl px-8 py-4 text-base font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:opacity-95"
            style={{
              background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
            }}
          >
            Start free
            <ChevronRight className="ml-1 h-5 w-5" />
          </Link>
          <Link
            to="/how-it-works"
            className="inline-flex items-center justify-center rounded-xl border-2 border-[#E5EAF0] px-8 py-4 text-base font-semibold text-gray-700 transition-all hover:border-genie-primary/40 hover:text-genie-primary"
          >
            See how it works
          </Link>
        </div>
      </div>
    </section>
  );
}
